/**
 * BankAccount Service
 * 
 * Service for handling bank account-related API calls.
 */

import { apiConfig } from "@/config/api";
import { apiFetch } from "@/lib/apiFetch";

export interface BankAccount {
  _id: string;
  bank: string | {
    _id: string;
    name: string;
    code: string;
  };
  accountNumber: string;
  accountName: string;
  tipe?: "ORDA" | "ORLOK";
  branch?: string;
  province?: string;
  city?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBankAccountInput {
  bank: string; // Bank ID
  accountNumber: string;
  accountName: string;
  tipe?: "ORDA" | "ORLOK";
  province?: string;
  city?: string;
  isActive?: boolean;
}

export interface UpdateBankAccountInput {
  bank?: string;
  accountNumber?: string;
  accountName?: string;
  tipe?: "ORDA" | "ORLOK";
  province?: string;
  city?: string;
  isActive?: boolean;
}

export interface BankAccountListResponse {
  data: BankAccount[];
}

export interface BankAccountResponse {
  data: BankAccount;
}

export interface ValidateBankAccountInput {
  accountNumber: string;
  bankShortCode?: string;
  bankId?: string;
}

export interface ValidateBankAccountResponse {
  status?: number;
  error?: string | null;
  metaData?: {
    source?: string;
    entity?: string;
    [key: string]: any;
  };
  data?: {
    message?: string;
    bankShortCode?: string;
    accountNo?: string;
    accountName?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

class BankAccountServiceClass {
  private baseURL = `${apiConfig.baseURL}/api/bank-accounts`;

  async getAllBankAccounts(bank?: string, isActive?: boolean): Promise<BankAccount[]> {
    // Handle both absolute and relative URLs
    let url: string;
    if (this.baseURL.startsWith('http://') || this.baseURL.startsWith('https://')) {
      // Absolute URL
      const urlObj = new URL(this.baseURL);
      if (bank) {
        urlObj.searchParams.append("bank", bank);
      }
      if (isActive !== undefined) {
        urlObj.searchParams.append("isActive", String(isActive));
      }
      url = urlObj.toString();
    } else {
      // Relative URL - construct manually
      const params = new URLSearchParams();
      if (bank) {
        params.append("bank", bank);
      }
      if (isActive !== undefined) {
        params.append("isActive", String(isActive));
      }
      const queryString = params.toString();
      url = queryString ? `${this.baseURL}?${queryString}` : this.baseURL;
    }

    const response = await apiFetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to fetch bank accounts" }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data: BankAccountListResponse = await response.json();
    return data.data;
  }

  async getBankAccountById(id: string): Promise<BankAccount> {
    const response = await apiFetch(`${this.baseURL}/${id}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to fetch bank account" }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data: BankAccountResponse = await response.json();
    return data.data;
  }

  async createBankAccount(input: CreateBankAccountInput): Promise<BankAccount> {
    const response = await apiFetch(this.baseURL, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to create bank account" }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data: BankAccountResponse = await response.json();
    return data.data;
  }

  async updateBankAccount(id: string, input: UpdateBankAccountInput): Promise<BankAccount> {
    const response = await apiFetch(`${this.baseURL}/${id}`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to update bank account" }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data: BankAccountResponse = await response.json();
    return data.data;
  }

  async deleteBankAccount(id: string): Promise<BankAccount> {
    const response = await apiFetch(`${this.baseURL}/${id}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Failed to delete bank account" }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data: BankAccountResponse = await response.json();
    return data.data;
  }

  async validateBankAccount(input: ValidateBankAccountInput): Promise<ValidateBankAccountResponse> {
    const response = await apiFetch(`${this.baseURL}/validate`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    });

    const data: ValidateBankAccountResponse = await response.json().catch(() => {
      throw new Error("Failed to parse validation response");
    });

    // Return the response even if it contains an error, let the component handle it
    // The Brick API may return 200 with an error field
    if (!response.ok && !data.error) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }

    return data;
  }
}

export const bankAccountService = new BankAccountServiceClass();

